import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import localitiesApis from '@/apis/localities'
import useIsMobile from '@/hooks/useIsMobile'
import Breadcrumb from '@/routes/store/bread-crum'
import ShopCategory from '@/tabs/locality/ShopCategory'
import LocalityCard from '@/tabs/locality/LocalityCard'
import CityPageSkelton from '@/tabs/locality/CityPageSkelton'
import { formatLocation, getSEOFriendlyURL } from '@/lib/utilities/Utils'
import ExploreShops from '@/tabs/locality/ExploreShops'
import Head from 'next/head'

interface StoreLocalityWithCount {
  locality: string
  storeCount: number
}
interface CategoryBasedOnCity {
  storeTypeId: number
  storeTypeName: string
  image: string
  storeCategoryCount: number
  header: string
  subHeader: string
  storeLocalityWithCount: StoreLocalityWithCount[]
}

interface CityProps {
  categoryLevelStoreCount: CategoryBasedOnCity[]
  totalStoreCount: number
}

const normalizeCategory = (value: string): string =>
  value
    ?.toLowerCase()
    .replace(/\s*&\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const Cities = (): JSX.Element => {
  const isMobile = useIsMobile()
  const router = useRouter()
  const { city, storeCategory } = router.query

  const [loadingState, setLoadingState] = useState('loading')
  const [cityData, setCityData] = useState<CityProps | null>(null)
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedCategoryLocality, setSelectedCategoryLocality] = useState<CategoryBasedOnCity | null>(null)

  useEffect(() => {
    if (cityData === null) return
    setSelectedCategory(storeCategory as string)

    const selectedStoreCategory =
      cityData?.categoryLevelStoreCount?.find(
        (item) =>
          normalizeCategory(item?.storeTypeName) ===
          normalizeCategory(formatLocation(storeCategory as string))
      )
    setSelectedCategoryLocality(selectedStoreCategory ?? null)
  }, [storeCategory, cityData])

  useEffect(() => {
    if (
      city !== undefined && city !== null && city !== ''
    ) {
      setLoadingState('loading')
      localitiesApis.getLocalities(
        {
          cityName: formatLocation(city as string)
        }
      ).then((res) => {
        setCityData(res?.data)
        setLoadingState('loaded')
      }).catch(() => {
        setLoadingState('error')
      })
    }
  }, [city])

  return (
    <>
      {/* SEO Content */}
      <Head>
        <title>
          {selectedCategoryLocality?.header !== undefined
            ? `${selectedCategoryLocality?.header} | LoveLocal`
            : `Explore Local Shops in ${formatLocation(city as string)} | LoveLocal`}
        </title>
        <meta
          name="description"
          content={selectedCategoryLocality?.subHeader ??
            `Discover trusted local shops near you. Select a category to explore popular localities in ${formatLocation(city)}.`}
        />
        {
          city !== undefined && city !== null && city !== '' && (
            <>
              <link
                rel="canonical"
                href={`https://www.lovelocal.in/${getSEOFriendlyURL(
                  city as string
                )}${storeCategory !== undefined ? `?storeCategory=${storeCategory as string}` : ''}`}
              />
            </>
          )
        }
      </Head>

      <div className={`container ${isMobile ? '' : 'mx-auto'}`}>
        {(() => {
          let showcase = <></>
          if (loadingState === 'loading') {
            showcase = <CityPageSkelton />
          } else if (loadingState === 'error') {
            showcase = (
              <div className='flex justify-center items-center my-2'>
                <p className='text-red-500 text-lg font-bold'>Something went wrong. Please try again.</p>
              </div>
            )
          } else if (loadingState === 'loaded' && cityData !== null) {
            showcase = (
              <div className='pt-4'>
                <div className={`${isMobile ? 'mx-4' : ''}`}>
                  <Breadcrumb
                    country="india"
                    city={city as string}
                    cityTitle={formatLocation(city as string)}
                    storeType={selectedCategory}
                    storeTypeTitle={formatLocation(selectedCategory)}
                    isLocalityPage
                  />
                </div>
                {
                  selectedCategoryLocality !== null && (
                    <div className='mb-8 ml-3'>
                      <h1 className='text-xl md:text-2xl leading-tight font-bold text-black mb-1'>
                        {selectedCategoryLocality?.header ?? ''}
                      </h1>
                      <h2 className='text-base leading-tight text-gray-600'>
                        {selectedCategoryLocality?.subHeader ?? ''}
                      </h2>
                    </div>
                  )
                }
                <div className={`flex ${isMobile ? 'flex-col' : 'gap-14'}`}>
                  <div className={`${isMobile ? 'w-full' : ''}`}>
                    <ShopCategory
                      storeCategory={cityData?.categoryLevelStoreCount ?? []}
                      selectedCategory={selectedCategory}
                    />
                  </div>
                  <div className="mb-4 flex-1 min-w-0">
                    {
                      selectedCategoryLocality === null && cityData?.categoryLevelStoreCount?.length > 0
                        ? <ExploreShops city={city as string} />
                        : (
                          <div>
                            {
                              cityData?.categoryLevelStoreCount?.length > 0 && (
                                <div
                                  className={`flex  mb-6
                                    ${isMobile ? 'mt-5 mx-4 flex-col justify-start' : 'items-center justify-between'}`
                                  }
                                >
                                  <h2 className='text-lg font-bold text-black'>
                                    Popular Localities in {' '}
                                    <span className='font-bold text-black'>{formatLocation(city as string)}</span>
                                  </h2>
                                  <div>
                                    {selectedCategoryLocality !== null && selectedCategoryLocality?.storeCategoryCount > 0
                                      ? <span className='text-gray-600'>
                                          {`${selectedCategoryLocality?.storeCategoryCount > 100
                                            ? `${selectedCategoryLocality?.storeCategoryCount}+`
                                            : selectedCategoryLocality?.storeCategoryCount} shops found`}
                                        </span>
                                      : ''}
                                  </div>
                                </div>
                              )
                            }
                            {
                              cityData?.totalStoreCount > 0
                                ? (
                                  <div
                                    className={`${isMobile
                                      ? 'flex flex-col gap-4 mx-4'
                                      : 'grid grid-cols-2 gap-6 max-h-[600px]'}
                                      overflow-y-scroll no-scrollbar`
                                    }
                                  >
                                    {selectedCategoryLocality?.storeLocalityWithCount?.map((locality) => (
                                      <LocalityCard
                                        key={locality.locality}
                                        city={city as string}
                                        localityName={locality.locality}
                                        storeCount={locality.storeCount}
                                        storeTypeName={selectedCategoryLocality?.storeTypeName ?? ''}
                                      />
                                    ))
                                    }
                                  </div>
                                  )
                                : (
                                    // Replace with actual UI
                                    <div className='mx-4 flex justify-center items-center my-2'>
                                      <p className='text-lg text-center'>No localities found in {' '}
                                        <span className='font-bold text-black'>{formatLocation(city as string)}.
                                        </span>
                                        <div>
                                          Select a different city to explore popular localities and shops
                                        </div>
                                      </p>
                                    </div>
                                  )
                            }
                          </div>
                          )
                    }
                  </div>
                </div>
              </div>
            )
          }
          return showcase
        })()}
      </div>
    </>
  )
}

export default Cities
